import type { ComponentProps } from 'react'
import { cn } from '@/lib/utils'

/* DataTable primitives.

   ONE THING TO KNOW. Cells do NOT wrap by default — the client's tables are
   nowrap, which keeps rows scannable but means a wide grid overflows its card
   silently rather than reflowing. This cost three fixes during the screen
   build. Either shorten the label, or pass `wrap` on the cell that carries
   prose. The wrapper below scrolls horizontally so an overflow degrades into a
   scrollbar rather than into clipped, invisible columns. */

export function TableWrap({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('w-full overflow-x-auto', className)} {...props} />
}

export function Table({ className, ...props }: ComponentProps<'table'>) {
  return <table className={cn('w-full border-collapse text-sm', className)} {...props} />
}

export function THead({ className, ...props }: ComponentProps<'thead'>) {
  return (
    <thead
      className={cn('[&_tr]:bg-secondary dark:[&_tr]:bg-[#1e3a5f]', className)}
      {...props}
    />
  )
}

export function TH({ className, numeric, ...props }: ComponentProps<'th'> & { numeric?: boolean }) {
  return (
    <th
      scope="col"
      className={cn(
        'h-10 px-4 py-2.5 text-xs font-medium whitespace-nowrap text-primary',
        'dark:text-[#90caf9]',
        numeric ? 'text-end tnum' : 'text-start',
        className,
      )}
      {...props}
    />
  )
}

export function TBody({ className, ...props }: ComponentProps<'tbody'>) {
  return (
    <tbody
      className={cn(
        '[&_tr:hover]:bg-[rgb(240_249_255/0.6)] dark:[&_tr:hover]:bg-[#16283d]',
        '[&_tr:last-child_td]:border-b-0',
        className,
      )}
      {...props}
    />
  )
}

export function TD(
  { className, numeric, wrap, ...props }:
  ComponentProps<'td'> & { numeric?: boolean; wrap?: boolean },
) {
  return (
    <td
      className={cn(
        'px-4 py-3 align-middle border-b border-border',
        wrap ? 'whitespace-normal' : 'whitespace-nowrap',
        numeric && 'text-end tnum',
        className,
      )}
      {...props}
    />
  )
}
